import { motion } from "framer-motion";
import { Check, ArrowUpRight, Clock } from "lucide-react";
import { LINKS, IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.1 },
  }),
};

export const Membership = () => {
  const CONTENT = useSettings();
  const { chapter, heading, subtitle, tiers } = CONTENT.membership;

  return (
    <section id="membership" className="relative py-28 md:py-36 bg-zinc-950 overflow-hidden" data-testid="membership">
      <img src={IMAGES.roadTrees} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-[0.04]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-16">
          <motion.p
            variants={reveal} custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-xs tracking-[0.3em] uppercase text-amber-500 font-bold mb-5"
          >
            {chapter}
          </motion.p>
          <motion.h2
            variants={reveal} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="font-display font-extrabold uppercase tracking-tight text-4xl md:text-5xl leading-[0.95] text-navy-dark"
          >
            {heading}
          </motion.h2>
          <motion.p
            variants={reveal} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="mt-6 text-base md:text-lg text-zinc-400"
          >
            {subtitle}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl">
          {tiers.filter((t) => !t.hidden).map((t, i) => {
            const hasList = t.listPrice && t.listPrice !== t.salePrice;
            const features = (t.features || []).filter((f) => !f.hidden);
            return (
            <motion.div
              key={t.name}
              variants={reveal} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              className={`relative rounded-lg p-[1px] ${t.highlight ? "" : "bg-slate-200"}`}
              data-testid={`membership-tier-${i}`}
            >
              {t.highlight && <div className="absolute inset-0 rounded-lg trace-beam opacity-80" />}
              <div className={`relative h-full rounded-lg p-8 flex flex-col bg-white ${t.highlight ? "shadow-xl shadow-blue-500/10" : "shadow-lg shadow-slate-200/60"}`}>
                {t.highlight && (
                  <span className="absolute -top-3 left-8 bg-amber-500 text-zinc-950 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-sm">
                    Best Value
                  </span>
                )}
                <h3 className="font-display font-extrabold text-2xl uppercase tracking-tight text-navy-dark">{t.name}</h3>
                {t.subtitle && <p className="mt-1 text-sm text-zinc-500">{t.subtitle}</p>}

                <div className="mt-6 flex items-end gap-2.5">
                  <span className="font-display font-black text-5xl leading-none text-navy-dark">{t.salePrice || t.price}</span>
                  {hasList && <span className="text-lg text-zinc-500 line-through mb-1">{t.listPrice}</span>}
                </div>
                {t.savings && (
                  <span className="mt-3 w-fit inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-bold text-emerald-400" data-testid={`tier-${i}-savings`}>
                    {t.savings}
                  </span>
                )}
                {t.note && <p className="mt-4 text-sm text-zinc-400">{t.note}</p>}

                {features.length > 0 && (
                  <ul className="mt-7 space-y-3">
                    {features.map((f) => (
                      <li key={f.label} className="flex items-start gap-3 text-sm">
                        {f.comingSoon ? (
                          <Clock size={17} className="text-amber-500/70 shrink-0 mt-0.5" />
                        ) : (
                          <Check size={17} className="text-amber-500 shrink-0 mt-0.5" />
                        )}
                        <span className={f.comingSoon ? "text-zinc-500" : "text-zinc-300"}>
                          {f.label}
                          {f.comingSoon && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-amber-400/80">Coming Soon</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                <a
                  href={LINKS.signup}
                  className={`mt-8 group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md font-bold transition-colors ${
                    t.highlight
                      ? "bg-amber-500 text-white hover:bg-amber-600"
                      : "border border-navy/25 text-navy-dark hover:border-amber-500 hover:text-amber-500"
                  }`}
                  data-testid={`membership-cta-${i}`}
                >
                  {t.cta}
                  <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </motion.div>
          );})}
        </div>
      </div>
    </section>
  );
};
